/** Billing — Fortune-500 repackage (Feb 2026).
 *
 *  Blueprint Part 2:
 *    • GH₵ headline (big) + $ secondary (muted, derived from live FX)
 *    • Station-side tab vs Agency-side tab
 *    • Phase-2 features rendered with a muted pill instead of a check
 *    • Comparison table below the cards
 *    • Live rate footer: "rates update daily · GH₵{rate}/$1"
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NavBar } from "@/components/NavBar";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Button, Pill, Waveform, PriceTag } from "@/design";
import { api } from "@/lib/api";
import { T } from "@/lib/theme";

const C = T.dark;
const TRACK_COLORS = {
  station: "#0891B2",  // cyan-600
  agency: "#0D9488",   // teal-600
};

// ── Helpers ────────────────────────────────────────────────────────────────
function isPhase2(f) {
  return typeof f === "object" && f?.phase === "2";
}
function FeatureLine({ feature }) {
  if (typeof feature === "string") {
    return (
      <li style={{ display: "flex", gap: 10, padding: "5px 0", color: C.muted, fontSize: 13, lineHeight: 1.55 }}>
        <span style={{ color: "#10B981", flexShrink: 0, fontWeight: 700 }}>✓</span>
        <span>{feature}</span>
      </li>
    );
  }
  // Phase-2 row — uses design system <Pill variant="phase2">
  return (
    <li style={{ display: "flex", gap: 10, padding: "5px 0", fontSize: 13, lineHeight: 1.55, opacity: 0.75 }}>
      <Pill variant="phase2" data-testid="feature-phase2-pill">PHASE 2</Pill>
      <span style={{ color: C.muted }}>{feature.label}</span>
    </li>
  );
}

function PlanCard({ plan, cycle, onSubscribe, loading }) {
  const isFree = plan.id === "free";
  const isContact = plan.contact_sales;
  const isPhase2Full = plan.phase_2_full;
  const ghs = cycle === "annual" ? plan.annual_ghs : plan.monthly_ghs;
  const accent = TRACK_COLORS[plan.track] || C.cyan;
  return (
    <div data-testid={`plan-card-${plan.id}`}
      style={{
        background: C.surface,
        border: `1px solid ${plan.popular ? accent + "88" : C.border}`,
        borderRadius: 14, padding: 24, position: "relative",
        boxShadow: plan.popular ? `0 0 0 1px ${accent}44, 0 8px 32px ${accent}22` : "none",
        display: "flex", flexDirection: "column",
      }}>
      {plan.popular && (
        <div style={{ position: "absolute", top: -10, right: 16, background: accent, color: "#fff",
          padding: "3px 10px", fontSize: 10, fontWeight: 800, borderRadius: 999, letterSpacing: "0.06em" }}>
          MOST POPULAR
        </div>
      )}
      {isPhase2Full && (
        <div style={{ position: "absolute", top: -10, left: 16, background: "#F59E0B", color: "#fff",
          padding: "3px 10px", fontSize: 10, fontWeight: 800, borderRadius: 999, letterSpacing: "0.06em" }}>
          PHASE 2 · PREVIEW
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <h3 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 24, fontWeight: 800, margin: 0, color: C.text }}>
          {plan.name}
        </h3>
        <span style={{ background: accent + "22", color: accent, fontSize: 9, fontWeight: 800,
          padding: "2px 7px", borderRadius: 999, letterSpacing: "0.08em" }}>
          {plan.track === "station" ? "STATION" : "AGENCY"}
        </span>
      </div>
      <p style={{ color: C.muted, fontSize: 12, margin: "0 0 16px", minHeight: 32, lineHeight: 1.5 }}>{plan.tagline}</p>

      <div style={{ marginBottom: 16 }}>
        {isContact ? (
          <>
            <div style={{ fontSize: 22, fontWeight: 800, color: C.text, fontFamily: "'IBM Plex Mono',monospace" }}>Custom</div>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>Contact our sales team</div>
          </>
        ) : isFree ? (
          <>
            <div style={{ fontSize: 32, fontWeight: 800, color: "#10B981", fontFamily: "'IBM Plex Mono',monospace" }}>Free</div>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>Forever — no card required</div>
          </>
        ) : (
          <div data-testid={`plan-price-${plan.id}`}>
            <PriceTag ghs={ghs} usd={cycle === "annual" ? plan.annual_usd : plan.monthly_usd}
              suffix={cycle === "annual" ? "/yr" : "/mo"} tone="dark" />
          </div>
        )}
      </div>

      {plan.audience_blurb && (
        <div style={{ padding: "8px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 16 }}>
          {plan.audience_blurb}
        </div>
      )}

      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 22px", flex: 1 }}>
        {(plan.features || []).map((f, i) => (
          <FeatureLine key={i} feature={f} />
        ))}
      </ul>

      <Button data-testid={`plan-cta-${plan.id}`}
        size="md" fullWidth disabled={loading || isPhase2Full}
        variant={plan.popular ? "primary" : "secondary"}
        audience={plan.track === "station" ? "station" : "agency"}
        loading={loading}
        onClick={() => onSubscribe(plan)}>
        {isPhase2Full ? "Coming Phase 2" : (plan.cta || "Choose")}
      </Button>
    </div>
  );
}

// ── Comparison table — distilled feature × tier matrix ────────────────────
const COMPARISON_ROWS = [
  { label: "Live audience pulse", track: "both", values: { free: "✓ your station", starter: "✓ full", pro_station: "✓ full", insights: "✓ market-wide", agency: "✓ market-wide", enterprise: "✓ all" } },
  { label: "Demographics depth", track: "station", values: { free: "—", starter: "✓ G × A × LSM", pro_station: "✓ + region", insights: "✓ market view", agency: "✓ all", enterprise: "✓ all" } },
  { label: "History window", track: "both", values: { free: "7 days", starter: "60 days", pro_station: "12 months", insights: "60 days", agency: "12 months", enterprise: "Unlimited" } },
  { label: "Market rankings", track: "both", values: { free: "Blurred", starter: "—", pro_station: "✓", insights: "Read-only", agency: "✓", enterprise: "✓" } },
  { label: "Transmission certificates", track: "both", values: { free: "—", starter: "✓", pro_station: "✓", insights: "—", agency: "Unlimited", enterprise: "Unlimited" } },
  { label: "Airtime Value Planning Tool", track: "agency", values: { free: "Public quote", starter: "—", pro_station: "—", insights: "✓", agency: "✓", enterprise: "✓" } },
  { label: "AdVerify SSE ticker", track: "agency", values: { free: "—", starter: "—", pro_station: "✓", insights: "—", agency: "✓", enterprise: "✓" } },
  { label: "Stations / brands included", track: "both", values: { free: "1", starter: "1", pro_station: "3", insights: "—", agency: "Unlimited", enterprise: "Unlimited" } },
  { label: "Users / seats", track: "both", values: { free: "1", starter: "2", pro_station: "5", insights: "2", agency: "10", enterprise: "Unlimited" } },
  { label: "Embeddable widget", track: "station", values: { free: "—", starter: "—", pro_station: "✓", insights: "—", agency: "—", enterprise: "✓" } },
  { label: "Weekly WaveScore Report", track: "station", values: { free: "—", starter: "—", pro_station: "✓ PDF", insights: "—", agency: "—", enterprise: "✓" } },
  { label: "Panel-validated reach", track: "both", phase2: true, values: { free: "—", starter: "—", pro_station: "Phase 2", insights: "—", agency: "Phase 2", enterprise: "Phase 2" } },
  { label: "Brand-mention intelligence", track: "agency", phase2: true, values: { free: "—", starter: "—", pro_station: "—", insights: "—", agency: "—", enterprise: "Phase 2" } },
  { label: "API access", track: "agency", values: { free: "—", starter: "—", pro_station: "—", insights: "—", agency: "—", enterprise: "✓" } },
  { label: "Support", track: "both", values: { free: "Community", starter: "Email + WA", pro_station: "Priority", insights: "Email", agency: "Priority", enterprise: "Dedicated CSM" } },
];

function ComparisonCell({ value, isPhase2Row }) {
  if (value === "✓" || value?.startsWith?.("✓")) {
    return <span style={{ color: "#10B981", fontWeight: 700, fontSize: 12 }}>{value}</span>;
  }
  if (value === "Phase 2") {
    return <span style={{ background: "#F59E0B22", color: "#F59E0B", fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 999, letterSpacing: "0.06em" }}>PHASE 2</span>;
  }
  if (value === "—") return <span style={{ color: C.dim, fontSize: 12 }}>—</span>;
  return <span style={{ color: isPhase2Row ? "#F59E0B" : C.muted, fontSize: 12 }}>{value}</span>;
}

export default function Billing() {
  const { user } = useAuth();
  const { rate, source } = useCurrency();
  const nav = useNavigate();
  const [data, setData] = useState({ plans: [], annual_savings_pct: 17 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cycle, setCycle] = useState("monthly");
  const [track, setTrack] = useState("station");  // station | agency
  // F-110 — self-serve cancel state
  const [sub, setSub] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) { setSub(null); return; }
    api.get("/billing/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, [user]);

  const visiblePlans = useMemo(() => {
    // Free goes in both tracks. Otherwise filter by track.
    return (data.plans || []).filter((p) => p.id === "free" || p.track === track);
  }, [data.plans, track]);

  const visibleComparison = useMemo(
    () => COMPARISON_ROWS.filter((r) => r.track === "both" || r.track === track),
    [track]
  );

  const subscribe = async (plan) => {
    if (plan.id === "free") return nav("/register");
    if (plan.contact_sales) return window.location.assign("mailto:sales@wavescorepro.com?subject=WaveScorePro Enterprise enquiry");
    if (plan.phase_2_full) return;  // Phase-2 waitlist (TODO: email capture)
    if (!user) return nav("/register");
    setLoading(true); setError("");
    try {
      const { data: pay } = await api.post("/payments/initiate", {
        plan: plan.id, email: user.email, billing_cycle: cycle,
      });
      if (pay.authorization_url) window.location.href = pay.authorization_url;
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="billing-page" style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Inter',sans-serif" }}>
      <style>{`body{margin:0;background:${C.bg}} @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&family=IBM+Plex+Mono:wght@500;600&display=swap')`}</style>
      <NavBar dark />

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "44px 24px 80px" }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 42, fontWeight: 800, margin: "0 0 8px", letterSpacing: "-0.02em" }}>
            Pricing that proves its value.
          </h1>
          <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.6, maxWidth: 680, margin: "0 auto 26px" }}>
            All prices in Ghana cedi. Charged via Paystack — your bank shows GH₵.
            USD shown for reference at the live rate.
          </p>

          {/* Track toggle */}
          <div data-testid="track-toggle" style={{ display: "inline-flex", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 4, marginRight: 12 }}>
            {[
              ["station", "📻 For Stations"],
              ["agency", "🎯 For Agencies"],
            ].map(([k, label]) => (
              <button key={k} data-testid={`track-${k}`} onClick={() => setTrack(k)}
                style={{ padding: "9px 18px", background: track === k ? TRACK_COLORS[k] : "transparent",
                  color: track === k ? "#fff" : C.muted, border: "none", borderRadius: 8,
                  cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{label}</button>
            ))}
          </div>

          {/* Cycle toggle */}
          <div data-testid="billing-cycle-toggle" style={{ display: "inline-flex", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 4 }}>
            {[["monthly", "Monthly"], ["annual", `Annual · save ${data.annual_savings_pct || 17}%`]].map(([k, label]) => (
              <button key={k} data-testid={`cycle-${k}`} onClick={() => setCycle(k)}
                style={{ padding: "9px 16px", background: cycle === k ? "#10B981" : "transparent",
                  color: cycle === k ? "#fff" : C.muted, border: "none", borderRadius: 8,
                  cursor: "pointer", fontSize: 13, fontWeight: 700 }}>{label}</button>
            ))}
          </div>
        </div>

        {error && <div data-testid="billing-error" style={{ color: "#EF4444", textAlign: "center", marginBottom: 18, fontSize: 13 }}>⚠ {error}</div>}

        {/* F-110 — Current subscription banner (only for signed-in users on a paid plan) */}
        {sub && sub.can_cancel && (
          <CurrentSubscriptionBanner
            sub={sub}
            onCancel={() => setCancelOpen(true)}
          />
        )}
        {sub && sub.cancelled_at && (
          <CancelledBanner sub={sub} onResubscribe={() => {}} />
        )}

        {/* Plan grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 16 }}>
          {visiblePlans.map((p) => (
            <PlanCard key={p.id} plan={p} cycle={cycle} onSubscribe={subscribe} loading={loading} />
          ))}
        </div>

        {/* Comparison table */}
        <div data-testid="comparison-table" style={{ marginTop: 48, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
          <div style={{ padding: "18px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <h2 style={{ margin: 0, fontFamily: "'Space Grotesk',sans-serif", fontSize: 20, fontWeight: 800, color: C.text }}>Feature comparison</h2>
            <span style={{ color: C.muted, fontSize: 12 }}>{track === "station" ? "📻 Station-side plans" : "🎯 Agency / Brand-side plans"}</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 720 }}>
              <thead>
                <tr style={{ background: C.bg }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", color: C.muted, fontSize: 11, letterSpacing: "0.06em", fontWeight: 700, textTransform: "uppercase" }}>Feature</th>
                  {visiblePlans.map((p) => (
                    <th key={p.id} style={{ padding: "12px 12px", color: C.text, fontSize: 12, fontWeight: 700 }}>
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleComparison.map((row, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${C.border}`, background: i % 2 === 0 ? "transparent" : C.bg + "66" }}>
                    <td style={{ padding: "10px 16px", color: row.phase2 ? "#F59E0B" : C.text, fontWeight: 500, fontSize: 12 }}>
                      {row.label}
                      {row.phase2 && <span style={{ marginLeft: 8, background: "#F59E0B22", color: "#F59E0B", fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 999, letterSpacing: "0.06em" }}>PHASE 2</span>}
                    </td>
                    {visiblePlans.map((p) => (
                      <td key={p.id} style={{ padding: "10px 12px", textAlign: "center" }}>
                        <ComparisonCell value={row.values[p.id] ?? "—"} isPhase2Row={row.phase2} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Phase-2 note */}
        <div data-testid="billing-phase2-note" style={{ marginTop: 24, padding: "14px 20px",
          background: "#F59E0B11", border: `1px solid #F59E0B55`, borderRadius: 12,
          color: "#FCD34D", fontSize: 12, lineHeight: 1.6, textAlign: "center", maxWidth: 760, margin: "24px auto 0" }}>
          <strong style={{ color: "#F59E0B" }}>🔬 Phase-2 (Q2 2026):</strong>{" "}
          Panel-Validated reach, Brand-Intelligence (share-of-voice + ad-mention detection), and full API access become live add-ons.
          Pro & Agency subscribers get first access at launch — no price changes.
        </div>

        {/* FX footer */}
        <div data-testid="billing-fx-footer" style={{ textAlign: "center", marginTop: 32, color: C.muted, fontSize: 11, lineHeight: 1.7 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <Waveform width={36} height={18} bars={5} tone="dark" muted />
            Rates update every 6 hours · GH₵{rate.toFixed(2)}/$1 ·{" "}
            <span style={{ opacity: 0.6 }}>source: {source}</span>
          </div>
          <br />
          Need help choosing? <a data-testid="billing-talk" href="mailto:sales@wavescorepro.com" style={{ color: "#10B981", fontWeight: 600 }}>Talk to us →</a>
        </div>
      </div>

      {/* F-110 — Cancellation confirmation modal */}
      {cancelOpen && sub && (
        <CancelModal
          sub={sub}
          onClose={() => setCancelOpen(false)}
          onCancelled={(newSub) => {
            setSub(newSub);
            setCancelOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// F-110 — subscription-management sub-components
// ─────────────────────────────────────────────────────────────────────────────
function CurrentSubscriptionBanner({ sub, onCancel }) {
  const expires = sub.expires_at ? new Date(sub.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—";
  return (
    <div data-testid="current-subscription" style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
      padding: "20px 24px", marginBottom: 26, display: "flex",
      flexWrap: "wrap", gap: 20, alignItems: "center", justifyContent: "space-between",
    }}>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>Current plan</div>
          <div data-testid="current-plan-name" style={{ fontSize: 18, fontWeight: 800, color: C.text, fontFamily: "'Space Grotesk',sans-serif" }}>
            {sub.plan_name} <span style={{ color: C.muted, fontWeight: 500, fontSize: 13 }}>· {sub.billing_cycle}</span>
          </div>
        </div>
        <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: 20 }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>Renews on</div>
          <div style={{ fontSize: 14, color: C.text, fontWeight: 600 }}>{expires}</div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <a data-testid="billing-change-plan" href="#plans" style={{
          padding: "10px 18px", background: "transparent", color: C.cyan,
          border: `1px solid ${C.cyan}55`, borderRadius: 8, fontSize: 13,
          fontWeight: 700, textDecoration: "none",
        }}>Change plan ↓</a>
        <button data-testid="billing-cancel-btn" onClick={onCancel} style={{
          padding: "10px 18px", background: "transparent", color: "#F87171",
          border: `1px solid #F8717155`, borderRadius: 8, fontSize: 13,
          fontWeight: 700, cursor: "pointer",
        }}>Cancel plan</button>
      </div>
    </div>
  );
}

function CancelledBanner({ sub }) {
  const expires = sub.expires_at ? new Date(sub.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—";
  return (
    <div data-testid="cancelled-banner" style={{
      background: "#F59E0B11", border: `1px solid #F59E0B55`, borderRadius: 14,
      padding: "16px 22px", marginBottom: 26, color: "#FCD34D",
      fontSize: 13, lineHeight: 1.6,
    }}>
      <strong style={{ color: "#F59E0B" }}>⏳ Your {sub.plan_name} plan is cancelled.</strong>{" "}
      You keep full access until <strong>{expires}</strong>, then your account moves to the free plan.
      To keep your subscription, pick any plan below to resubscribe.
    </div>
  );
}

function CancelModal({ sub, onClose, onCancelled }) {
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | error
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!password) { setErr("Password is required."); return; }
    setState("sending"); setErr("");
    try {
      await api.post("/billing/cancel", { confirm_password: password, reason: reason || null });
      const { data: fresh } = await api.get("/billing/subscription");
      onCancelled(fresh);
    } catch (e) {
      setState("idle");
      setErr(e.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <div data-testid="cancel-modal" role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(10,22,40,0.78)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
          padding: 28, maxWidth: 480, width: "100%", color: C.text }}>
        <h2 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 800, fontFamily: "'Space Grotesk',sans-serif" }}>
          Cancel {sub.plan_name}?
        </h2>
        <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, margin: "0 0 20px" }}>
          You&apos;ll keep full access until{" "}
          <strong style={{ color: C.text }}>
            {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "the end of the current period"}
          </strong>, then move to the free plan. No refund for partial months.
        </p>

        <label style={{ display: "block", fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
          Confirm your password
        </label>
        <input data-testid="cancel-password" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{ width: "100%", padding: "10px 12px", background: C.bg, border: `1px solid ${C.border}`,
            borderRadius: 6, fontSize: 13, color: C.text, outline: "none", boxSizing: "border-box" }} />

        <label style={{ display: "block", fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 14, marginBottom: 4 }}>
          Reason (optional)
        </label>
        <textarea data-testid="cancel-reason" value={reason} rows={3} maxLength={500}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Help us improve — what could we have done better?"
          style={{ width: "100%", padding: "10px 12px", background: C.bg, border: `1px solid ${C.border}`,
            borderRadius: 6, fontSize: 13, color: C.text, outline: "none", boxSizing: "border-box",
            fontFamily: "inherit", resize: "vertical" }} />

        {err && <div data-testid="cancel-error" style={{ color: "#F87171", fontSize: 12,
          padding: "8px 10px", background: "#7F1D1D33", borderRadius: 6, marginTop: 12 }}>{err}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
          <button data-testid="cancel-keep-btn" onClick={onClose}
            style={{ padding: "10px 18px", background: "transparent", color: C.text,
              border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 700,
              cursor: "pointer" }}>Keep my plan</button>
          <button data-testid="cancel-confirm-btn" onClick={submit} disabled={state === "sending"}
            style={{ padding: "10px 20px", background: "#DC2626", color: "#fff",
              border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700,
              cursor: "pointer", opacity: state === "sending" ? 0.6 : 1 }}>
            {state === "sending" ? "Cancelling…" : "Cancel subscription"}
          </button>
        </div>
      </div>
    </div>
  );
}
